import { call, select } from 'redux-saga/effects';

import { mapPathToPageView } from '../../../utils';
import {
  CoachmarkTypes,
  Events,
  mapActionTypeToStatsActionType,
  StatsActions,
  ViewModes,
} from '../../constants';
import * as D from '../../deps';
import * as StatsUtils from '../../utils';

import { logEvents, logRecentlyPlayedCarouselEvent } from './index';

const { AUDIO_PLAY_TRACK } = D.Constants.AudioPlayer.AudioPlayerActions;
const { SHUFFLE_TRACKS } = D.Constants.PlaylistT3.ActionTypes;
const { STATION_TRIGGER } = D.Constants.SourceCard.SourceCardTriggerTypes;
const { ADDTOCOLLECTION, ADDTOPLAYLIST } = D.Constants.SourceCard.SourceCardConstants;
const defaultActionType = StatsActions.ROUTE;

const mapBackstageActionTypeToStatsActionType = {
  [D.Constants.ViewActions.SHOWED_BACKSTAGE_PAGE]: StatsActions.ACCESS,
  [D.Constants.Integrations.ActionTypes.COMPONENT_VIEWED]: StatsActions.VIEW,
  [D.Constants.Integrations.ActionTypes.COMPONENT_CLICKED]: defaultActionType,
};

export function* getBackstageComponentActionType(action) {
  const { pandoraId } = action.payload;
  const componentEventType = action.payload.componentEventType || action.payload.optionName;

  if (componentEventType) {
    // Handle click events
    return yield call(getActionType, pandoraId, componentEventType);
  }

  return mapBackstageActionTypeToStatsActionType[action.type]; // Handle view events
}

export function getActionTypeOnPlay({
  pandoraType,
  isPremiumUser,
  isStationType,
  isSourceUnavailable,
}) {
  if (pandoraType === D.Constants.SourceTypes.PandoraTypes.ARTIST) {
    return !isPremiumUser
      ? CoachmarkTypes.PREMIUM_ACCESS
      : mapActionTypeToStatsActionType[AUDIO_PLAY_TRACK];
  } else if (isSourceUnavailable || pandoraType === D.Constants.SourceTypes.PandoraTypes.PODCAST) {
    return StatsActions.INSUFFICIENT_RIGHTS_TOAST;
  } else if (!isPremiumUser) {
    return isStationType
      ? mapActionTypeToStatsActionType[AUDIO_PLAY_TRACK]
      : CoachmarkTypes.PREMIUM_ACCESS;
  }
  return mapActionTypeToStatsActionType[AUDIO_PLAY_TRACK];
}

export function* getActionType(pandoraId, componentEventType) {
  const details = yield select(D.getInDomain('catalog.utils.getAnnotations'), pandoraId);
  const pandoraType = D.Utils.SourceType.getPandoraTypeFromPandoraId(pandoraId);
  const isStationType = [
    D.Constants.SourceTypes.PandoraTypes.STATION,
    D.Constants.SourceTypes.PandoraTypes.GENRE,
  ].includes(pandoraType);
  const isSourceUnavailable =
    D.Utils.Rights.isRadioOnly(details) || D.Utils.Rights.isUnavailable(details);
  const isPodcastUnavailable =
    D.Utils.SourceType.isPodcastPandoraType(pandoraType) && D.Utils.Rights.isRetired(details);
  const isPremiumUser = yield select(D.Utils.Listener.hasPremiumFeatures);
  const actionType = mapActionTypeToStatsActionType[componentEventType] || componentEventType;

  if (actionType === mapActionTypeToStatsActionType[AUDIO_PLAY_TRACK]) {
    return getActionTypeOnPlay({
      pandoraType,
      isPremiumUser,
      isStationType,
      isSourceUnavailable,
      isPodcastUnavailable,
    });
  } else if (actionType === mapActionTypeToStatsActionType[SHUFFLE_TRACKS]) {
    return !isPremiumUser ? CoachmarkTypes.PREMIUM_ACCESS : actionType;
  } else if (actionType === mapActionTypeToStatsActionType[STATION_TRIGGER]) {
    return pandoraType === D.Constants.SourceTypes.PandoraTypes.ALBUM
      ? StatsActions.START_STATION_TOAST
      : actionType;
  } else if (actionType === mapActionTypeToStatsActionType[ADDTOCOLLECTION]) {
    return isSourceUnavailable ? StatsActions.INSUFFICIENT_RIGHTS_TOAST : actionType;
  } else if (actionType === mapActionTypeToStatsActionType[ADDTOPLAYLIST]) {
    return isSourceUnavailable ? StatsActions.INSUFFICIENT_RIGHTS_TOAST : actionType;
  }
  return actionType;
}

export function* getBackstagePandoraIdFromUrl(path) {
  const details = yield select(
    D.getInDomain('catalog.utils.getAnnotationByShareableUrlPath'),
    path,
  );
  if (!details.isEmpty()) {
    return details.get('pandoraId');
  } else if (D.Utils.Route.isBackstageGenrePath(path)) {
    const genreSlug = path.substring(path.lastIndexOf('/') + 1);
    const genre = yield select(D.Utils.Music.getGenre, genreSlug);
    return genre ? genre.get('musicId') : genreSlug;
  }

  // if annotations aren't added to the catalog state, check if it exists else where (ie. playlist state)
  const { pandoraId } = yield select(
    D.Utils.Route.getPandoraIdAndSourceTypeFromBackstagePath,
    path,
  );
  return pandoraId;
}

export function* getBackstageComponentDestinationId({
  payload: { destinationId, destinationUrl, pandoraId },
}) {
  if (D.Utils.Route.isBackstagePath(destinationUrl)) {
    return yield call(getBackstagePandoraIdFromUrl, destinationUrl);
  } else if (D.Utils.Route.isProfilePath(destinationUrl)) {
    const details = yield select(D.Utils.Music.getPlaylistFromState, pandoraId);

    if (details) {
      return details.get('listenerPandoraId', null);
    }
  }

  return destinationId;
}

export function* getBackstageSourceData({ payload }) {
  const currentPath = yield select(D.Utils.Route.getCurrentPathnameFromState);
  const previousPath = yield select(D.getInDomain('application.utils.getPreviousPath'));
  const pandoraId = yield call(getBackstagePandoraIdFromUrl, currentPath);

  const currentPageViewSource = currentPath ? yield select(mapPathToPageView, currentPath) : '';
  const previousPageViewSource = previousPath ? yield select(mapPathToPageView, previousPath) : '';
  return {
    pageId: payload.pageId || pandoraId,
    currentPageViewSource,
    previousPageViewSource,
  };
}

export function* isBackstagePageView() {
  const currentPath = yield select(D.Utils.Route.getCurrentPathnameFromState);
  const currentPageViewSource = currentPath ? yield select(mapPathToPageView, currentPath) : '';
  if (currentPageViewSource) {
    const isCollectedStationPage = currentPageViewSource.startsWith('station_details');

    return (
      currentPageViewSource.startsWith('backstage') ||
      currentPageViewSource.startsWith('playlist') ||
      isCollectedStationPage
    );
  }
}

export function* onBackstageEvent(action) {
  if (!action.payload) {
    // TODO-PAYLOAD: WEBCA-2514
    action.payload = action.data;
  }

  const contentSection = action.payload.contentSection;
  const isBackstageView = yield call(isBackstagePageView);

  if (!isBackstageView) {
    if (!StatsUtils.isBackstageContentSection(contentSection)) {
      const isBackstageClickAction =
        action.type === D.Constants.Integrations.ActionTypes.COMPONENT_CLICKED;
      const isCarouselSection =
        contentSection ===
        D.Constants.ContentSections.CollectionContentSections.RECENTLY_PLAYED_CAROUSEL;

      if (isBackstageClickAction && isCarouselSection) {
        yield call(logRecentlyPlayedCarouselEvent, action);
      }
    }
    return;
  }

  const currentIndex = action.payload.currentIndex;
  const destinationId = yield call(getBackstageComponentDestinationId, action);
  const actionType = yield call(getBackstageComponentActionType, action);
  const { pageId, currentPageViewSource, previousPageViewSource } = yield call(
    getBackstageSourceData,
    action,
  );
  const isAnon = yield select(D.Utils.Listener.isAnonymousListener);
  const searchToken = yield select(D.getInDomain('searchT3.selectors.getSearchToken', null));

  const viewMode = isAnon ? ViewModes.ANONYMOUS : ViewModes.STANDARD;

  const hasPlayablePrograms = action.payload.hasCuratorPrograms;

  // hasPlayablePrograms indicates in the access event if an artist or curator has playlists or stations
  const shouldDataIncludeHasPlayablePrograms =
    hasPlayablePrograms !== null &&
    (currentPageViewSource === 'backstage_artist' ||
      currentPageViewSource === 'backstage_curator') &&
    actionType === 'access';

  const eventData = {
    action: actionType,
    destination_id: destinationId,
    from_pandora: `${!!previousPageViewSource}`,
    index: currentIndex,
    page_id: pageId,
    page: currentPageViewSource,
    search_server_response_token: searchToken,
    section: contentSection,
    source: previousPageViewSource,
    view_mode: viewMode,
    web_client: 'true',
    ...(shouldDataIncludeHasPlayablePrograms && { has_playable_programs: hasPlayablePrograms }),
  };

  yield D.tryCall(logEvents, [
    {
      eventName: Events.EVENT_BACKSTAGE,
      eventData,
    },
  ]);
}
